/**
 * Sidebar Store - Zustand Implementation
 *
 * Migrated from custom SidebarRegistry to use Zustand for state management
 * Benefits:
 * - Less boilerplate code
 * - Built-in DevTools support
 * - Better TypeScript inference
 * - Automatic re-rendering optimization
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { createLogger } from '../utils/logger';

const logger = createLogger('SidebarStore');

export interface SidebarItem {
  id: string;
  label: string;
  icon: string;
  route: string;
  section?: 'main' | 'explore' | 'admin' | string;
  order?: number;
  visible?: () => boolean | Promise<boolean>;
  badge?: () => string | number | null;
  children?: SidebarItem[];
}

export interface SidebarSection {
  id: string;
  title?: string;
  order: number;
  items: SidebarItem[];
}

interface SidebarState {
  // State
  items: Map<string, SidebarItem>;
  sections: Map<string, SidebarSection>;

  // Actions
  registerItem: (item: SidebarItem) => () => void;
  unregisterItem: (itemId: string) => void;
  registerSection: (section: SidebarSection) => void;
  getSections: () => SidebarSection[];
  getItemsBySection: (sectionId: string) => SidebarItem[];
  _initDefaultSections: () => void;
}

export const useSidebarStore = create<SidebarState>()(
  devtools(
    (set, get) => ({
      // Initial state
      items: new Map(),
      sections: new Map(),

      // Initialize default sections
      _initDefaultSections: () => {
        const defaultSections: SidebarSection[] = [
          { id: 'main', title: undefined, order: 0, items: [] },
          { id: 'explore', title: 'Explore', order: 1, items: [] },
          { id: 'admin', title: 'Admin', order: 2, items: [] },
        ];

        const newSections = new Map<string, SidebarSection>();
        defaultSections.forEach(section => {
          newSections.set(section.id, section);
        });

        set({ sections: newSections });
      },

      // Register a sidebar item
      registerItem: (item: SidebarItem) => {
        logger.debug(`Registering item: ${item.id}`);

        set((state) => {
          // Clone maps to trigger Zustand update
          const newItems = new Map(state.items);
          const newSections = new Map(state.sections);

          // Add item to items map
          newItems.set(item.id, item);

          // Remove existing instances from any section
          newSections.forEach(section => {
            section.items = section.items.filter(existing => existing.id !== item.id);
          });

          // Add to appropriate section
          const sectionId = item.section || 'main';
          const section = newSections.get(sectionId);

          if (section) {
            section.items.push(item);
            section.items.sort((a, b) => (a.order || 0) - (b.order || 0));
          }

          return { items: newItems, sections: newSections };
        });

        // Return unregister function
        return () => get().unregisterItem(item.id);
      },

      // Unregister item
      unregisterItem: (itemId: string) => {
        set((state) => {
          const newItems = new Map(state.items);
          const newSections = new Map(state.sections);

          newItems.delete(itemId);

          // Remove from all sections
          newSections.forEach(section => {
            section.items = section.items.filter(item => item.id !== itemId);
          });

          return { items: newItems, sections: newSections };
        });
      },

      // Register a section
      registerSection: (section: SidebarSection) => {
        set((state) => {
          const newSections = new Map(state.sections);
          newSections.set(section.id, section);
          return { sections: newSections };
        });
      },

      // Get all sections sorted by order
      getSections: () => {
        const state = get();
        return Array.from(state.sections.values())
          .sort((a, b) => a.order - b.order);
      },

      // Get items for a specific section
      getItemsBySection: (sectionId: string) => {
        const state = get();
        const section = state.sections.get(sectionId);
        return section?.items || [];
      },
    }),
    { name: 'SidebarStore' }
  )
);

// Initialize default sections on module load
useSidebarStore.getState()._initDefaultSections();

/**
 * React Hook for using sidebar sections
 *
 * Usage:
 *   const sections = useSidebarSections()
 *
 * Zustand automatically handles subscriptions and re-rendering
 */
export function useSidebarSections() {
  return useSidebarStore((state) => state.getSections());
}

/**
 * Hook to get items for a specific section
 */
export function useSidebarSection(sectionId: string) {
  return useSidebarStore((state) => state.getItemsBySection(sectionId));
}

/**
 * Hook to register a sidebar item
 * Returns the registerItem function
 */
export function useRegisterSidebarItem() {
  return useSidebarStore((state) => state.registerItem);
}
